/**
 * Route guard component — gates all AppLayout children behind Entra ID auth.
 *
 * Unauthenticated visitors are sent to loginRedirect via a useEffect, not in
 * the render path — keeping render pure and avoiding double-fire under React 18
 * StrictMode. The intended path is preserved via the MSAL `state` parameter so
 * the redirect target is restored after sign-in completes (§11.3).
 *
 * Applied at the single AppLayout route element in routes/index.tsx so that
 * all children are gated with no route-list churn.
 *
 * MsalProvider (wrapping the whole tree) awaits handleRedirectPromise before
 * children render (via initMsal in main.tsx), so RequireAuth always
 * evaluates post-redirect — useIsAuthenticated() is stable on mount.
 */

import React, { useEffect } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { apiScopes } from '@/config/msalConfig'

interface RequireAuthProps {
  children: React.ReactNode
}

/**
 * Renders children only when the user is authenticated with an active MSAL
 * account. Triggers loginRedirect (not loginPopup) from a useEffect otherwise —
 * full-page redirect preserves the deep-link URL via MSAL state. The effect
 * is guarded on InteractionStatus.None to prevent duplicate redirects while
 * a prior interaction is already in flight.
 */
const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()

  useEffect(() => {
    // Guard: fire only when not yet authenticated and no MSAL interaction is
    // already in progress. Without the inProgress guard, StrictMode's double
    // invocation or a concurrent render could trigger a second loginRedirect
    // before the first completes, causing a redirect loop.
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance
        .loginRedirect({
          scopes: apiScopes,
          // MSAL serialises window.location.href into `state` and restores it
          // after the redirect completes — no manual path-preservation needed.
          state: window.location.href,
        })
        .catch((err) => {
          console.error('loginRedirect failed:', err)
        })
    }
  }, [isAuthenticated, inProgress, instance])

  if (!isAuthenticated) {
    // Render the interstitial while the redirect is being initiated.
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-ui-canvas"
        role="status"
        aria-live="polite"
        aria-label="Redirecting to Microsoft sign-in"
      >
        <p className="text-body text-ui-text-muted">
          Redirecting to Microsoft…
        </p>
      </div>
    )
  }

  return <>{children}</>
}

RequireAuth.displayName = 'RequireAuth'

export default RequireAuth
